import React, { useState, useEffect, useMemo } from 'react';
import {
  Target,
  Search,
  Brain,
  User,
  ArrowLeft,
  Sparkles,
  BookOpen,
  Lightbulb,
  MessageSquare,
  ChevronRight,
  X,
  Star,
  Bookmark,
  Compass,
  Building
} from 'lucide-react';

// Import existing interview components
import StrategicEssayTopics from '../StrategicEssayTopicsV2';
import HiddenCompetenciesInterview from '../EssayIdeas/HiddenCompetenciesInterview';
import CharacterIdentityInterview from '../EssayIdeas/CharacterIdentityInterview';
import MomentsThatMatterInterview from '../EssayIdeas/MomentsThatMatterInterview';
import NegotiationPersuasionInterview from '../EssayIdeas/NegotiationPersuasionInterview';
import SentimentalObjectsGenerator from '../EssayIdeas/SentimentalObjectsGenerator';
import EssayIdeasLibrary from './EssayIdeasLibrary';
import WhySchoolBuilder from '../WhySchool/WhySchoolBuilder';

const EssayIdeasHub = ({
  assessmentData,
  personalProfile,
  colleges = [],
  onCreateEssay,
  onClose,
  directMode, // New prop for direct bypass
  selectedIdeaId, // ID of essay idea selected from sidebar
  hideLibrary = false, // New prop to hide library card
  userId, // User ID for Why School Builder
  essayId, // Essay ID for Why School Builder
  collegeId // College ID for Why School Builder
}) => {
  console.log('🎯 EssayIdeasHub Props:', { 
    hasAssessmentData: !!assessmentData, 
    hasPersonalProfile: !!personalProfile,
    collegesCount: colleges.length,
    directMode,
    selectedIdeaId,
    hideLibrary
  });
  
  const [selectedModule, setSelectedModule] = useState(null);
  const [libraryRefreshKey, setLibraryRefreshKey] = useState(0);
  const [localSelectedIdeaId, setLocalSelectedIdeaId] = useState(selectedIdeaId);

  // Module card configurations - matching the mockup design
  const modules = useMemo(() => {
    const allModules = [
      // Only include library if not hidden
      ...(!hideLibrary ? [{
        id: 'library',
        title: 'My Saved Ideas',
        description: 'View and develop all your saved essay topics from interviews and assessments',
        secondaryText: 'Browse, search, and manage your essay ideas',
        dotColor: 'bg-emerald-500',
        badgeText: 'YOUR IDEAS',
        badgeColor: 'bg-emerald-100 text-emerald-700',
        component: EssayIdeasLibrary,
        available: true
      }] : []),
    {
      id: 'why-school',
      title: 'Why School / Major Builder',
      description: 'Structured research-to-draft workflow for college-specific supplemental essays',
      secondaryText: '7-section guided process with AI-powered suggestions',
      dotColor: 'bg-sky-500',
      badgeText: 'SUPPLEMENTAL',
      badgeColor: 'bg-sky-100 text-sky-700',
      component: WhySchoolBuilder,
      available: true,
      isBuilder: true // Flag to handle differently
    },
    {
      id: 'strategic',
      title: 'Strategic Assessment Topics',
      description: 'Essay topics based on your assessment responses - develop stories you already have',
      secondaryText: 'AI-curated topics based on your profile',
      dotColor: 'bg-blue-500',
      badgeText: 'PERSONALIZED',
      badgeColor: 'bg-blue-100 text-blue-700',
      component: StrategicEssayTopics,
      available: !!assessmentData,
      mode: 'assessment'
    },
    {
      id: 'untold-stories',
      title: 'Untold Stories',
      description: 'Narrative opportunities found in-between-the-lines of your analysis',
      secondaryText: 'Explore values and experiences that define you',
      dotColor: 'bg-red-500',
      badgeText: '',
      badgeColor: '',
      component: StrategicEssayTopics,
      available: !!assessmentData,
      mode: 'insights'
    },
    {
      id: 'browse',
      title: 'Moments That Matter',
      description: 'Discover essay topics in the details you notice and moments you remember',
      secondaryText: 'Memory-based interview revealing how you see and process the world',
      dotColor: 'bg-amber-500',
      badgeText: 'THINK BACK',
      badgeColor: 'bg-amber-100 text-amber-700',
      component: MomentsThatMatterInterview,
      available: true
    },
    {
      id: 'competencies',
      title: 'Hidden Competencies',
      description: 'AI-guided interview to uncover your unique strengths and unexpected stories',
      secondaryText: 'Uncover hidden strengths through conversation',
      dotColor: 'bg-teal-500',
      badgeText: 'AI INTERVIEW',
      badgeColor: 'bg-teal-100 text-teal-700',
      component: HiddenCompetenciesInterview,
      available: true
    },
    {
      id: 'identity',
      title: 'Character & Identity',
      description: 'Deep-dive interview exploring your values, experiences, and what makes you unique',
      secondaryText: 'Comprehensive exploration of your identity',
      dotColor: 'bg-yellow-500',
      badgeText: 'DEEP DIVE',
      badgeColor: 'bg-yellow-100 text-yellow-700',
      component: CharacterIdentityInterview,
      available: true
    },
    {
      id: 'brainstorm',
      title: 'Quick Brainstorm',
      description: '10-minute rapid-fire session to generate essay angles you haven\'t considered',
      secondaryText: 'Fast idea generation for immediate inspiration',
      dotColor: 'bg-pink-500',
      badgeText: '',
      badgeColor: '',
      component: null, // Will implement QuickBrainstorm later
      available: true
    },
    {
      id: 'negotiation_persuasion',
      title: 'Negotiation & Persuasion',
      description: 'Reveal your tactical approach to influence, conflict, and power dynamics',
      secondaryText: 'Detailed breakdown of how you handle persuasion across different contexts',
      dotColor: 'bg-purple-500',
      badgeText: 'INFLUENCE',
      badgeColor: 'bg-purple-100 text-purple-700',
      component: NegotiationPersuasionInterview,
      available: true
    },
    {
      id: 'essay_generator',
      title: 'Essay Idea Generator',
      description: 'Find essay topics through objects that matter to you',
      secondaryText: 'Progressive prompts unlock as you complete each session',
      dotColor: 'bg-indigo-500',
      badgeText: 'OBJECTS',
      badgeColor: 'bg-indigo-100 text-indigo-700',
      component: SentimentalObjectsGenerator,
      available: true
    }
  ];
  
  return allModules;
  }, [assessmentData, hideLibrary]); // Dependencies for useMemo

  const handleModuleClick = (module) => {
    console.log('🔍 Module clicked:', module.id, 'Available:', module.available, 'Component:', !!module.component);
    console.log('📊 Assessment data present:', !!assessmentData);
    
    if (!module.available) {
      console.warn('⚠️ Module not available:', module.id);
      // Show a helpful message if assessment data is missing
      if ((module.id === 'strategic' || module.id === 'untold-stories') && !assessmentData) {
        alert('Please complete your strategic assessment first to unlock personalized essay topics.');
      }
      return;
    }
    
    if (module.component) {
      setSelectedModule(module);
    } else if (module.id === 'brainstorm') {
      // For now, show a placeholder for Quick Brainstorm
      setSelectedModule({
        ...module,
        component: QuickBrainstormPlaceholder
      });
    } else {
      console.warn('⚠️ Module missing component:', module.id);
    }
  };

  const handleBack = () => {
    setSelectedModule(null);
  };
  
  // Handle completion of interviews - refresh library when new ideas are saved
  const handleInterviewComplete = (result) => {
    console.log('✅ Interview completed, triggering library refresh');
    // Increment refresh key to force Library to refetch
    setLibraryRefreshKey(prev => prev + 1);
    
    // If navigation to library is requested (e.g., from "Save to Ideas" button)
    if (result?.navigateToLibrary) {
      console.log('📚 Navigating to library with selected idea:', result.selectedIdeaId);
      const libraryModule = modules.find(m => m.id === 'library');
      if (libraryModule) {
        // Small delay to ensure the library refresh happens before navigation
        setTimeout(() => {
          setSelectedModule(libraryModule);
          // The selectedIdeaId will be handled by the library component's props
          if (result.selectedIdeaId) {
            // Store the selected ID so it can be passed to the library
            setLocalSelectedIdeaId(result.selectedIdeaId);
          }
        }, 100);
      }
    }
  };

  // Handle direct bypass mode from URL
  useEffect(() => {
    if (directMode && modules) {
      const targetModule = modules.find(m =>
        (directMode === 'insights' && m.id === 'untold-stories') ||
        (directMode === 'assessment' && m.id === 'strategic')
      );

      if (targetModule && targetModule.available) {
        console.log('🚀 Direct mode activated:', directMode, '-> selecting module:', targetModule.id);
        setSelectedModule(targetModule);
      }
    }
  }, [directMode, modules]);

  // Handle selectedIdeaId from sidebar - open library with pre-selected idea
  useEffect(() => {
    if (selectedIdeaId && modules) {
      const libraryModule = modules.find(m => m.id === 'library');
      if (libraryModule) {
        console.log('💡 Opening library with selected idea:', selectedIdeaId);
        setSelectedModule(libraryModule);
      }
    }
  }, [selectedIdeaId, modules]);

  // Placeholder for Quick Brainstorm (will be implemented later)
  const QuickBrainstormPlaceholder = () => (
    <div className="flex-1 bg-slate-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Ideas Hub
          </button>
        </div>

        <div className="text-center mb-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-slate-800 mb-2">Quick Brainstorm</h2>
          <p className="text-slate-600 text-sm px-2">
            10-minute rapid-fire session to generate essay angles you haven't considered
          </p>
        </div>

        <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-sm border border-stone-200 p-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lightbulb className="w-8 h-8 text-pink-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Coming Soon!</h3>
            <p className="text-slate-600 mb-4">
              We're building an AI-powered brainstorming session that will help you discover unique essay angles in just 10 minutes.
            </p>
            <button
              onClick={() => alert('Quick Brainstorm feature coming soon!')}
              className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
            >
              Get Notified When Ready
            </button>
          </div>
        </div>
      </div>
    </div>
  );


  // If a module is selected, render its component
  if (selectedModule) {
    const ModuleComponent = selectedModule.component;

    return (
      <div className="flex-1 bg-slate-50 flex flex-col">
        <div className="p-4 sm:p-6 lg:p-8">
          {/* Back button for all modules */}
          <div className="mb-6 max-w-[1400px] mx-auto">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Ideas Hub
            </button>
          </div>

          {/* Render the selected component */}
          {ModuleComponent && (
            <div className="max-w-[1400px] mx-auto">
              {selectedModule.isBuilder ? (
                // Special case for Why School Builder
                <ModuleComponent
                  essayId={essayId}
                  collegeId={collegeId}
                  userId={userId}
                  essaySubtype="why_school"
                  onClose={handleBack}
                  onSave={() => {
                    handleBack();
                    // Parent component can refresh essay data if needed
                  }}
                />
              ) : (
                // Standard props for other interview modules
                <ModuleComponent
                  assessmentData={assessmentData}
                  insights={assessmentData?.formatted_insights?.insights || assessmentData?.insights}
                  personalProfile={personalProfile}
                  colleges={colleges}
                  onCreateEssay={onCreateEssay}
                  onDevelopIdea={(essay) => {
                    // Handle developing an essay idea
                    console.log('Develop essay idea:', essay);
                    if (onCreateEssay) {
                      onCreateEssay(essay);
                    }
                  }}
                onComplete={handleInterviewComplete} // Notify when interviews complete
                onBack={handleBack}
                onClose={handleBack}
                mode={selectedModule.mode} // Pass the mode to distinguish between assessment and insights
                selectedIdeaId={selectedModule.id === 'library' ? (localSelectedIdeaId || selectedIdeaId) : undefined} // Pass selectedIdeaId only to library
                refreshKey={selectedModule.id === 'library' ? libraryRefreshKey : undefined} // Pass refreshKey only to library
                />
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Get module references for sections
  const libraryModule = modules.find(m => m.id === 'library');
  const strategicModule = modules.find(m => m.id === 'strategic');
  const untoldStoriesModule = modules.find(m => m.id === 'untold-stories');
  const momentsModule = modules.find(m => m.id === 'browse');
  const competenciesModule = modules.find(m => m.id === 'competencies');
  const identityModule = modules.find(m => m.id === 'identity');
  const brainstormModule = modules.find(m => m.id === 'brainstorm');
  const generatorModule = modules.find(m => m.id === 'essay_generator');

  // Count of personalized topics
  const personalizedCount = assessmentData ? 12 : 0;

  // Main hub view - matching mockup design
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">


      {/* Discover Topics Section */}
      <div className="mb-8 sm:mb-12">
        <div className="bg-slate-900 border border-slate-900 rounded-xl overflow-hidden">
          <div className="px-4 sm:px-6 py-3 bg-slate-900">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-teal-400"></div>
              <h2 className="text-white font-bold text-sm uppercase tracking-wide">Discover Topics</h2>
            </div>
          </div>
          <div className="p-4 sm:p-6 bg-white">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">

              {/* Strategic Assessment Topics */}
              {strategicModule && (
                <button
                  onClick={() => handleModuleClick(strategicModule)}
                  className="text-left p-4 sm:p-6 bg-white border rounded-xl hover:shadow-lg hover:border-azure-600 transition-all group relative border-l-4 border-l-azure-500 w-full"
                >
                  {/* Recommended Badge */}
                  {strategicModule.available && (
                    <div className="absolute -top-3 -right-3 px-2 sm:px-3 py-1 bg-gold-400 border border-slate-900 rounded-lg shadow-lg">
                      <div className="flex items-center gap-1 sm:gap-1.5">
                        <Star className="w-3 h-3 text-slate-900 fill-slate-900" />
                        <span className="text-xs font-bold text-slate-900 uppercase tracking-wide">Start Here</span>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 mb-4">
                    <div className="w-10 h-10 bg-azure-600 border border-slate-900 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                      <Target className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="px-2 py-1 bg-azure-600 text-white text-xs font-bold rounded uppercase tracking-wide border border-azure-700">Personalized</span>
                      {personalizedCount > 0 && (
                        <span className="px-2 py-1 bg-azure-100 text-azure-800 text-xs font-bold rounded border border-azure-300">
                          {personalizedCount} topics
                        </span>
                      )}
                    </div>
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2">{strategicModule.title}</h3>
                  <p className="text-sm text-slate-700 mb-3">{strategicModule.description}</p>
                  <div className="flex items-center gap-2 text-xs text-stone-600">
                    <div className="w-2 h-2 rounded-full bg-azure-600 flex-shrink-0"></div>
                    <span>{strategicModule.secondaryText}</span>
                  </div>
                </button>
              )}

              {/* Untold Stories */}
              {untoldStoriesModule && (
                <button
                  onClick={() => handleModuleClick(untoldStoriesModule)}
                  className="text-left p-4 sm:p-6 bg-white border hover:border-coral-500 rounded-xl hover:shadow-lg transition-all group border-l-4 border-l-coral-400 w-full"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 mb-4">
                    <div className="w-10 h-10 bg-coral-500 border border-slate-900 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <ChevronRight className="w-4 h-4 text-coral-500 group-hover:translate-x-1 transition-transform mt-1" />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2">{untoldStoriesModule.title}</h3>
                  <p className="text-sm text-slate-700 mb-3">{untoldStoriesModule.description}</p>
                  <div className="flex items-center gap-2 text-xs text-stone-600">
                    <div className="w-2 h-2 rounded-full bg-coral-500 flex-shrink-0"></div>
                    <span>{untoldStoriesModule.secondaryText}</span>
                  </div>
                </button>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* Guided Discovery Section */}
      <div className="mb-8 sm:mb-12">
        <div className="bg-slate-900 border border-slate-900 rounded-xl overflow-hidden">
          <div className="px-4 sm:px-6 py-3 bg-slate-900">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-coral-400"></div>
              <h2 className="text-white font-bold text-sm uppercase tracking-wide">Guided Discovery</h2>
            </div>
          </div>
          <div className="p-4 sm:p-6 bg-white">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">

              {/* Moments That Matter */}
              {momentsModule && (
                <button
                  onClick={() => handleModuleClick(momentsModule)}
                  className="text-left p-4 sm:p-6 bg-white border hover:border-gold-500 rounded-xl hover:shadow-lg transition-all group border-l-4 border-l-gold-400 w-full"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 mb-4">
                    <div className="w-10 h-10 bg-gold-500 border border-slate-900 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                      <BookOpen className="w-5 h-5 text-slate-900" />
                    </div>
                    <span className="px-2 py-1 bg-gold-400 text-slate-900 text-xs font-bold rounded uppercase tracking-wide border border-gold-600 self-start">Think Back</span>
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2">{momentsModule.title}</h3>
                  <p className="text-sm text-slate-700 mb-3">{momentsModule.description}</p>
                  <div className="flex items-center gap-2 text-xs text-stone-600">
                    <div className="w-2 h-2 rounded-full bg-gold-500 flex-shrink-0"></div>
                    <span>Fast idea generation for inspiration</span>
                  </div>
                </button>
              )}

              {/* Hidden Competencies */}
              {competenciesModule && (
                <button
                  onClick={() => handleModuleClick(competenciesModule)}
                  className="text-left p-4 sm:p-6 bg-white border hover:border-teal-600 rounded-xl hover:shadow-lg transition-all group border-l-4 border-l-teal-500 w-full"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 mb-4">
                    <div className="w-10 h-10 bg-teal-600 border border-slate-900 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                      <MessageSquare className="w-5 h-5 text-white" />
                    </div>
                    <span className="px-2 py-1 bg-teal-500 text-white text-xs font-bold rounded uppercase tracking-wide border border-teal-700 self-start">AI Interview</span>
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2">{competenciesModule.title}</h3>
                  <p className="text-sm text-slate-700 mb-3">{competenciesModule.description}</p>
                  <div className="flex items-center gap-2 text-xs text-stone-600">
                    <div className="w-2 h-2 rounded-full bg-teal-600 flex-shrink-0"></div>
                    <span>Reveal your tactical approach</span>
                  </div>
                </button>
              )}

              {/* Character & Identity */}
              {identityModule && (
                <button
                  onClick={() => handleModuleClick(identityModule)}
                  className="text-left p-4 sm:p-6 bg-white border hover:border-indigo-500 rounded-xl hover:shadow-lg transition-all group border-l-4 border-l-indigo-400 w-full"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 mb-4">
                    <div className="w-10 h-10 bg-indigo-500 border border-slate-900 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <span className="px-2 py-1 bg-indigo-500 text-white text-xs font-bold rounded uppercase tracking-wide border border-indigo-700 self-start">Deep Dive</span>
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2">{identityModule.title}</h3>
                  <p className="text-sm text-slate-700 mb-3">{identityModule.description}</p>
                  <div className="flex items-center gap-2 text-xs text-stone-600">
                    <div className="w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0"></div>
                    <span>Progressive prompts unlock</span>
                  </div>
                </button>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* Quick Tools Section */}
      <div className="bg-white border border-slate-900 rounded-xl overflow-hidden">
        <div className="px-4 sm:px-6 py-4 bg-white border-b-2 border-azure-400">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-azure-500"></div>
            <h2 className="text-slate-900 font-bold text-sm uppercase tracking-wide">Quick Tools</h2>
          </div>
        </div>
        <div className="p-4 sm:p-6 bg-white">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">

            {/* Quick Brainstorm */}
            {brainstormModule && (
              <button
                onClick={() => handleModuleClick(brainstormModule)}
                className="text-left p-4 sm:p-6 bg-white border border-slate-300 rounded-xl hover:border-slate-900 hover:shadow-md transition-all group border-l-4 border-l-magenta-500 w-full"
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 mb-3">
                  <div className="w-8 h-8 bg-magenta-500 border border-slate-900 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-bold text-slate-900 text-base">{brainstormModule.title}</h3>
                    <div className="flex items-center gap-1 text-stone-500">
                      <Brain className="w-3.5 h-3.5" />
                      <span className="text-xs text-stone-600">10 min</span>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-slate-700 mb-3">{brainstormModule.description}</p>
                <div className="flex items-center gap-2 text-xs text-stone-600">
                  <div className="w-2 h-2 rounded-full bg-magenta-500 flex-shrink-0"></div>
                  <span>{brainstormModule.secondaryText}</span>
                </div>
              </button>
            )}

            {/* Essay Idea Generator */}
            {generatorModule && (
              <button
                onClick={() => handleModuleClick(generatorModule)}
                className="text-left p-4 sm:p-6 bg-white border border-slate-300 rounded-xl hover:border-slate-900 hover:shadow-md transition-all group border-l-4 border-l-orange-400 w-full"
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 mb-3">
                  <div className="w-8 h-8 bg-orange-400 border border-slate-900 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                    <Compass className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="font-bold text-slate-900 text-base">{generatorModule.title}</h3>
                </div>
                <p className="text-sm text-slate-700 mb-3">{generatorModule.description}</p>
                <div className="flex items-center gap-2 text-xs text-stone-600">
                  <div className="w-2 h-2 rounded-full bg-orange-400 flex-shrink-0"></div>
                  <span>{generatorModule.secondaryText}</span>
                </div>
              </button>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default EssayIdeasHub;